import { AddToPhotos } from '@mui/icons-material';
import { Button } from '@mui/material';
import * as Sentry from '@sentry/react';
import { Col, Progress, Row } from "antd";
import axios from "axios";
import { get } from "lodash";
import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { useCurrentUser } from "store/users/selectors";
import styled from "styled-components";
import { CloudFlarePreview } from "..";
import { useAlert } from '../alertSakeBar/alertProvider';
import { getAwsAuthToken } from "utils/lib/authHelper";
// /cloudflare/download-url is only called from the Download button (CloudFlarePreview).
// import { getCloudflareDownloadUrl } from "store/1wayteachervideo/thunks";
import sysConf from "config";

const PLAYBACK_POLL_MS = 5000;
const PLAYBACK_MAX_ATTEMPTS = 120;

const extractCloudflareVideoId = (url) => {
  const match = String(url || "").match(/cloudflarestream\.com\/([a-zA-Z0-9]+)/);
  return match ? match[1] : null;
};

const buildHlsManifestUrl = (videoIdOrUrl) => {
  const videoId = extractCloudflareVideoId(videoIdOrUrl) || videoIdOrUrl;
  if (!videoId) return null;
  const value = String(videoIdOrUrl || "");
  if (value.includes("cloudflarestream.com")) {
    try {
      const origin = new URL(value.startsWith("http") ? value : `https://${value}`).origin;
      return `${origin}/${videoId}/manifest/video.m3u8`;
    } catch {
      // fall through to default host
    }
  }
  return `https://customer-quvpuvq5vklyms5a.cloudflarestream.com/${videoId}/manifest/video.m3u8`;
};

async function isStreamManifestReady(manifestUrl) {
  try {
    const response = await fetch(manifestUrl, {
      method: "GET",
      mode: "cors",
      credentials: "omit",
      cache: "no-store",
    });
    if (!response.ok) return false;
    const body = await response.text();
    return body.includes("#EXTM3U");
  } catch {
    return false;
  }
}

async function waitForCloudflarePlaybackReady(videoIdOrUrl, showAlertFn) {
  const manifestUrl = buildHlsManifestUrl(videoIdOrUrl);
  if (!manifestUrl) return false;

  for (let attempts = 0; attempts < PLAYBACK_MAX_ATTEMPTS; attempts += 1) {
    if (await isStreamManifestReady(manifestUrl)) {
      return true;
    }
    await new Promise((resolve) => setTimeout(resolve, PLAYBACK_POLL_MS));
  }

  if (typeof showAlertFn === "function") {
    showAlertFn(
      "Cloudflare is still encoding this video. If you still see video not found, wait a minute and reopen this dialog.",
      "info",
    );
  }
  return false;
}

const fileTypes = ["MP4"];
const MAX_FILE_SIZE = 10 * 1024 * 1024;
// const API_URL = "localhost:4000/files/upload_to_cf";
const API_URL = `${sysConf.apiGateway.AI_BASE_NODE_URL}/files/upload_to_cf`;

const isCloudflareStreamUrl = (url) =>
  String(url || "").includes("cloudflarestream.com");

const CloudFlareUploader = forwardRef(({
  src = "",
  pageId = "",
  onChange = () => null,
  onUploadBusyChange,
  onUploadStart,
  onEncodingChange,
  headerReupload = false,
}, ref) => {
  const currentUser = useCurrentUser();
  const showAlert = useAlert();
  const [loader, setLoader] = useState(false);
  const [encodingPlayback, setEncodingPlayback] = useState(false);
  const [iframeUrl, setIframeUrl] = useState(src || "");
  const [loadingMapPercentage, setLoadingMapPercentage] = useState(0);
  const videoRef = useRef();
  const playerRef = useRef();
  const encodingPollIdRef = useRef(null);
  const reuploadInputRef = useRef(null);

  useEffect(() => {
    setIframeUrl(src || "");
  }, [src]);

  useEffect(() => {
    if (typeof onUploadBusyChange === "function") {
      onUploadBusyChange(loader);
    }
  }, [loader, onUploadBusyChange]);

  useEffect(() => {
    if (typeof onEncodingChange === "function") {
      onEncodingChange(encodingPlayback);
    }
  }, [encodingPlayback, onEncodingChange]);

  const startEncodingPoll = useCallback(
    (videoId) => {
      if (!videoId || encodingPollIdRef.current === videoId) {
        return;
      }
      encodingPollIdRef.current = videoId;
      setEncodingPlayback(true);
      void waitForCloudflarePlaybackReady(videoId, showAlert).finally(() => {
        if (encodingPollIdRef.current === videoId) {
          encodingPollIdRef.current = null;
        }
        setEncodingPlayback(false);
      });
    },
    [showAlert],
  );

  useEffect(() => {
    const videoId = extractCloudflareVideoId(src);
    if (!videoId) {
      setEncodingPlayback(false);
      return undefined;
    }
    if (encodingPollIdRef.current === videoId) {
      return undefined;
    }
    setEncodingPlayback(true);
    let cancelled = false;
    (async () => {
      try {
        // Do not call /cloudflare/download-url here — only on Download button click.
        // const response = await getCloudflareDownloadUrl(videoId);
        const manifestUrl = buildHlsManifestUrl(src);
        const ready = manifestUrl ? await isStreamManifestReady(manifestUrl) : false;
        if (cancelled) return;
        if (ready) {
          setEncodingPlayback(false);
          return;
        }
        startEncodingPoll(videoId);
      } catch (error) {
        Sentry.captureException(error);
        if (!cancelled) {
          startEncodingPoll(videoId);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [src, startEncodingPoll]);

  const validateUploader = (file) => {
    // Define accepted MIME types
    const acceptedTypes = {
      "video/mp4": "mp4",
      "video/quicktime": "mov", // MOV files have MIME type video/quicktime
    };

    const fileType = file.type.toLowerCase();

    if (!Object.keys(acceptedTypes).includes(fileType)) {
      showAlert(`Only ${fileTypes.join(" and ")} files are supported`, "error");
      return false;
    }

    if (file.size > MAX_FILE_SIZE && get(currentUser, "role") !== "owe_admin") {
      showAlert("File size exceeds maximum limit of 10MB", "error");
      return false;
    }

    return true;
  };

  const base64ToBlob = (base64, mime) => {
    const byteCharacters = atob(base64.split(",")[1]);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
      const slice = byteCharacters.slice(offset, offset + 1024);
      const byteNumbers = new Array(slice.length);

      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: mime });
  };

  const handleChange = (file) => {
    try {
      if (!validateUploader(file)) return;
      const reader = new FileReader();
      reader.readAsDataURL(file);
      handleUploader(file, pageId);
      reader.onloadend = function (e) {
        let mimeType = "";
        let base64String = reader.result;
        if (base64String.startsWith("data:video/mp4")) {
          mimeType = "video/mp4";
        } else if (base64String.startsWith("data:video/quicktime")) {
          mimeType = "video/quicktime";
        }
        const blob = base64ToBlob(base64String, mimeType);
        const url = URL.createObjectURL(blob);
        setIframeUrl(url);
      };
    } catch (error) {
      Sentry.captureException(`389 Error during handleChange: ${error}`);
      console.log(error);
    }
  };

  const handleReuploadClick = useCallback(() => {
    reuploadInputRef.current?.click();
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      openReuploadPicker: handleReuploadClick,
    }),
    [handleReuploadClick],
  );

  const handleReuploadFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      handleChange(file);
    }
    event.target.value = '';
  };

  const handleUploader = useCallback(async (file) => {
    setLoader(true);
    if (typeof onUploadStart === "function") {
      onUploadStart();
    }
    try {
      const formData = new FormData();
      const token = await getAwsAuthToken()
      formData.append("file", file);

      const config = {
        method: "post",
        maxBodyLength: Infinity,
        url: API_URL,
        headers: {
          "Content-Type": "multipart/form-data",
          "X-Amz-Date": new Date().toISOString(),
          "x-auth-source":sysConf.isDev?"test":"prod",
          'Authorization': `Bearer ${token}`,
          ...formData.getHeaders?.(),
        },
        data: formData,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setLoadingMapPercentage(percentCompleted);
        },
      };

      const response = await axios.request(config);
      const result = response?.data;
      const cfVideo = result?.video;

      if (cfVideo?.uid && cfVideo?.preview) {
        const resultUrl = await generateCloudflareStreamURL(result);
        if (resultUrl) {
          setIframeUrl(resultUrl);
          startEncodingPoll(cfVideo.uid);
          onChange({ url: resultUrl, pageId });
        }
      } else {
        setIframeUrl("");
        showAlert("cloudflare is not able to generate link", "error");
      }
    } catch (error) {
      Sentry.captureException(`390 Error during handleUploader: ${error}`);
      console.error("File upload failed", error);
    } finally {
      setLoader(false);
      setLoadingMapPercentage(0);
    }
  }, [onChange, onUploadStart, pageId, showAlert, startEncodingPoll]);

  useEffect(() => {
    if (iframeUrl && videoRef.current) {
      videoRef.current.load();
    }
  }, [iframeUrl]);
  const handlePreviewUrlChange = useCallback(
    ({ url }) => {
      if (!url) return;
      setIframeUrl(url);
      onChange({ url, pageId });
    },
    [onChange, pageId]
  );

  const generateCloudflareStreamURL = async (data) => {
    try {
      const result = data.video;

      // Extract UID and thumbnail URL
      const uid = result.uid;
      const thumbnailUrl = result.thumbnail;

      // Encode the thumbnail URL for use as a query parameter
      const encodedThumbnailUrl = encodeURIComponent(
        `${thumbnailUrl}?time=&height=600`
      );

      // Construct the final URL
      const baseUrl = `https://customer-quvpuvq5vklyms5a.cloudflarestream.com/${uid}/iframe`;
      const queryParams = `preload=true&loop=true&autoplay=true&poster=${encodedThumbnailUrl}`;

      return `${baseUrl}?${queryParams}`;
    } catch (error) {
      Sentry.captureException(`391 Error during generateCloudflareStreamURL: ${error}`);
      console.log(error);
    }
  };

  const previewIframeUrl =
    encodingPlayback || (loader && !isCloudflareStreamUrl(iframeUrl))
      ? ""
      : iframeUrl;
  const hasActiveVideo = Boolean(String(iframeUrl || "").trim());

  return (
    <>
      <ImageContainer onContextMenu={(e) => {}}>
        <Container>
          {!encodingPlayback && hasActiveVideo ? (
            <HiddenReuploadInput
              ref={reuploadInputRef}
              type="file"
              accept="video/mp4,video/quicktime,.mp4,.mov"
              onChange={handleReuploadFileChange}
            />
          ) : null}
          {!encodingPlayback && hasActiveVideo && !headerReupload ? (
            <ReuploadWrap>
              {loader ? (
                <Progress
                  type="line"
                  percent={loadingMapPercentage}
                  size="small"
                  className="fileProgress"
                  showInfo={false}
                />
              ) : null}
              <ReuploadButton
                variant="outlined"
                size="small"
                startIcon={<AddToPhotos />}
                onClick={handleReuploadClick}
                disabled={loader}
              >
                Re-upload
              </ReuploadButton>
            </ReuploadWrap>
          ) : null}
          <VideoContainer>
            <CloudFlarePreview
              iframeUrl={previewIframeUrl}
              onChange={handlePreviewUrlChange}
              pageId={pageId}
            />
          </VideoContainer>
          {!encodingPlayback && !hasActiveVideo ? (
              <FileUploader
                classes="file-uploader-cover video-uploader-modal-cover"
                handleChange={handleChange}
                name="file"
                types={fileTypes}
              >
                <FileUploadArea>
                  {loader ? (
                    <Progress
                      type="line"
                      percent={loadingMapPercentage}
                      size="small"
                      className="fileProgress"
                      showInfo={false}
                    />
                  ) : null}
                  <Row
                    className="box box-1"
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Column>
                      <AddToPhotos color="primary" />
                    </Column>
                    <Column>
                      <Text>Upload or drop a file right here</Text>
                    </Column>
                    <Column>
                      <Text>{fileTypes.join(",")}</Text>
                    </Column>
                  </Row>
                </FileUploadArea>
              </FileUploader>
          ) : null}
        </Container>
      </ImageContainer>
    </>
  );
});

CloudFlareUploader.displayName = 'CloudFlareUploader';

export default memo(CloudFlareUploader);
const ImageContainer = styled.div`
  height: auto;
  flex: 1;
  overflow: visible;
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-item: center;
  justify-content: center;
  position: relative;
  background-color: ${(p) => (p.empty ? "rgba(0,0,0,0.1)" : "transparent")};

  .video-uploader-modal-cover {
    position: absolute !important;
    top: 40% !important;
    left: 10% !important;
    width: 80% !important;
    z-index: 99 !important;
    margin-top: 10px !important;
    cursor: pointer;
    background: rgb(244 239 239 / 86%);
  }
`;

const HiddenReuploadInput = styled.input`
  display: none;
`;

const ReuploadWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 8px;
`;

const ReuploadButton = styled(Button)`
  background-color: rgba(0, 0, 0, 0.7) !important;
  color: #03989e !important;
  border: 1px solid #03989e !important;
  text-transform: none;
  font-size: 12px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.8) !important;
    border-color: #03989e !important;
  }

  &:disabled {
    color: #03989e !important;
    border-color: #03989e !important;
    opacity: 0.7;
  }

  .MuiButton-startIcon,
  .MuiSvgIcon-root {
    color: #03989e !important;
  }
`;

const FileUploadArea = styled.div`
  height: 100%;
  border: 1px dashed gray;
  padding: 5px 12px;
  min-height: 50px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Text = styled.div`
  font-size: 12px;
`;
const Column = styled(Col)`
display: flex;
    justify-content: center;
    align-items: center;
}
`;
const VideoContainer = styled.div`
  width: 100%;
  margin-bottom: 0px;
  background-color: #fff;
  border-radius: 8px;
  position: relative;
  padding-top: 56.25%;
  overflow: visible;
`;
